import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '../types'


// DXF Group Codes


/**
 * DXF Reader class
 * Used to read a DXF and process it into valid shapes
 */
export default class EditorDXFReader { 
  constructor(dxfContent, unitsScale = 1) {
    this.dxfData = []

    this.unitsScale = unitsScale
    
    this.dxfPairList = this._parseDXFContent(dxfContent)

    this.currentPairIndex = 0
  }


  /**
   * Parse the file contents into a DXF structure
   */
  _parseDXFContent(content, unitsScale) {
    if(!content)
      throw 'DXF file is invalid'

    var lines = (content || '').split("\n").map(line => line.trim())
    if(!lines || lines.length <= 1)
      throw 'DXF file is invalid'

    // Convert to DXF structure
    var pairList = []
    for(var lineIndex=0; lineIndex<lines.length; lineIndex += 2) {
      var code = parseInt(lines[lineIndex])
      var value = lines[lineIndex+1]

      pairList.push({
        code: code,
        value: value
      })
    }

    return pairList
  }


  /**
   * Parse a 2D position with X/Y coords into a Vector2 object
   * Note: Flips Y coordinate to match our coordinate system & adjusts for units scale
   */
  _parsePosition(attributes, xCoordCode, yCoordCode) {
    return new THREE.Vector2(parseFloat(attributes[xCoordCode]) * this.unitsScale, -parseFloat(attributes[yCoordCode]) * this.unitsScale)
  }


  /**
   * Parse a radius value. With units scaling
   */
  _parseRadius(value) {
    return parseFloat(value) * this.unitsScale
  }


  /**
   * Calculate the list of geometry entities from the DXF contents
   */
  _buildEntityList() {
    var entityList = []
    var blockList = []

    // Note: Currently blocks are disabled
    // INSERT block command doesn't currently support rotation/scale/basePoint offset
    let blocksEnabled = true

    var currentSection = ''
    var currentPolyline = null
    var currentBlock = null

    this.currentPairIndex = 0
    while(this.currentPairIndex < this.dxfPairList.length) {
      let pair = this._readNextPair()
      if(!pair)
        break

      if(pair.value == 'SECTION') {
        let nextPair = this._readNextPair()
        if(nextPair) {
          if(nextPair.code == 2)
            currentSection = nextPair.value
        }
      }
      else if(pair.value == 'BLOCK') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        let blockGroup = {
          name: attributes[2],
          blockName: attributes[3],
          description: attributes[4],
          layer: attributes[8],
          handle: attributes[5],
          flags: parseInt(attributes[70] || 0),
          basePoint: this._parsePosition(attributes, 10, 20),
          entityList: []
        }
        blockList.push(blockGroup)
        currentBlock = blockList[blockList.length - 1]
      }
      else if(pair.value == 'ENDBLK') {
        currentBlock = null
      }
      else if(pair.value == 'LINE') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        let lineEntity = {
          type: 'LINE',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          lineType: attributes[6],
          colourNumber: parseInt(attributes[62] || 0),
          startPosition: this._parsePosition(attributes, 10, 20),
          endPosition: this._parsePosition(attributes, 11, 21),
        }

        if(currentSection == 'ENTITIES') {
          entityList.push(lineEntity)
        } else if(currentSection == 'BLOCKS') {
          if(currentBlock)
            currentBlock.entityList.push(lineEntity)
        }
      }
      else if(pair.value == 'ARC') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        var arcEntity = {
          type: 'ARC',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          lineType: attributes[6],
          colourNumber: parseInt(attributes[62] || 0),
          arcCenterPosition: this._parsePosition(attributes, 10, 20),
          radius: this._parseRadius(attributes[40]),
          startAngle: 360 - parseFloat(attributes[50]),
          endAngle: 360 - parseFloat(attributes[51]),
          clockwise: false   // DXF is always counterclockwise
        }

        arcEntity.arcEllipseCurve = new THREE.EllipseCurve(
          arcEntity.arcCenterPosition.x,
          arcEntity.arcCenterPosition.y,
          arcEntity.radius,
          arcEntity.radius,
          MathsUtils.degreesToRadians(arcEntity.startAngle),
          MathsUtils.degreesToRadians(arcEntity.endAngle),
          !arcEntity.clockwise
        )

        arcEntity.startPosition = arcEntity.arcEllipseCurve.getPoint(0, new THREE.Vector2())
        arcEntity.endPosition = arcEntity.arcEllipseCurve.getPoint(1, new THREE.Vector2())

        if(currentSection == 'ENTITIES') {
          entityList.push(arcEntity)
        } else if(currentSection == 'BLOCKS') {
          if(currentBlock)
            currentBlock.entityList.push(arcEntity)
        }
      }
      else if(pair.value == 'CIRCLE') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        let circleEntity ={
          type: 'CIRCLE',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          lineType: attributes[6],
          colourNumber: parseInt(attributes[62] || 0),
          position: this._parsePosition(attributes, 10, 20),
          radius: this._parseRadius(attributes[40])
        }
        
        if(currentSection == 'ENTITIES') {
          entityList.push(circleEntity)
        } else if(currentSection == 'BLOCKS') {
          if(currentBlock)
            currentBlock.entityList.push(circleEntity)
        }
      }
      else if(pair.value == 'POINT') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        let pointEntity = {
          type: 'POINT',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          colourNumber: parseInt(attributes[62] || 0),
          position: this._parsePosition(attributes, 10, 20),
        }

        if(currentSection == 'ENTITIES') {
          entityList.push(pointEntity)
        } else if(currentSection == 'BLOCKS') {
          if(currentBlock)
            currentBlock.entityList.push(pointEntity)
        }
      }
      else if(pair.value == 'POLYLINE') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        let polylineEntity = {
          type: 'POLYLINE',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          colourNumber: parseInt(attributes[62] || 0),
          vertices: [],
          startPosition: null,
          endPosition: null,
        }

        if(currentSection == 'ENTITIES') {
          entityList.push(polylineEntity)
          currentPolyline = entityList[entityList.length - 1]
        } else if(currentSection == 'BLOCKS') {
          if(currentBlock) {
            currentBlock.entityList.push(polylineEntity)
            currentPolyline = currentBlock.entityList[currentBlock.entityList.length - 1]
          }
        }        
      }
      else if(pair.value == 'LWPOLYLINE') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        // Store 
        // Read as LWPOLYLINE, store as polyline
        let polylineEntity = {
          type: 'POLYLINE',
          name: attributes[2],
          layer: attributes[8],
          handle: attributes[5],
          colourNumber: parseInt(attributes[62] || 0),
          vertices: [],
          vertexData: [],
          startPosition: null,
          endPosition: null,
        }

        // Parse vertices
        polylineEntity.vertexData = this._parseLWPolylineVertexData(attributes, entityPairs)
        polylineEntity.vertices = this._convertLWPolylineVertices(polylineEntity.vertexData)

        if(polylineEntity.vertices.length > 0) {
          polylineEntity.startPosition = polylineEntity.vertices[0].clone()
          polylineEntity.endPosition = polylineEntity.vertices[polylineEntity.vertices.length - 1].clone()
        }

        if(polylineEntity.vertices.length > 0) {
          if(currentSection == 'ENTITIES') {
            entityList.push(polylineEntity)
          } else if(currentSection == 'BLOCKS') {
            if(currentBlock)
              currentBlock.entityList.push(polylineEntity)
          }
        }
      }
      else if(pair.value == 'SPLINE') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        // Convert Spline to Polyline
        let polylineEntity = this._convertSplineToPolylineEntity(attributes, entityPairs)

        if(polylineEntity.vertices.length > 0) {
          polylineEntity.startPosition = polylineEntity.vertices[0].clone()
          polylineEntity.endPosition = polylineEntity.vertices[polylineEntity.vertices.length - 1].clone()

          if(currentSection == 'ENTITIES') {
            entityList.push(polylineEntity)
            currentPolyline = entityList[entityList.length - 1]
          } else if(currentSection == 'BLOCKS') {
            if(currentBlock) {
              currentBlock.entityList.push(polylineEntity)
              currentPolyline = currentBlock.entityList[currentBlock.entityList.length - 1]
            }
          }
        }
      }
      else if(pair.value == 'VERTEX') {
        let entityPairs = this._readEntityAttributePairs()
        let attributes = this._parseEntityAttributes(entityPairs)

        if(currentPolyline) {
          currentPolyline.vertices.push(this._parsePosition(attributes, 10, 20))
          if(currentPolyline.vertices.length > 0) {
            currentPolyline.startPosition = currentPolyline.vertices[0].clone()
            currentPolyline.endPosition = currentPolyline.vertices[currentPolyline.vertices.length - 1].clone()
          }
        }
      }
      else if(pair.value == 'SEQEND') {
        currentPolyline = null
      }
      else if(pair.value == 'INSERT') {
        // Note: Currently don't support inserting blocks into blocks
        if(blocksEnabled && currentSection == 'ENTITIES') {
          let entityPairs = this._readEntityAttributePairs()
          let attributes = this._parseEntityAttributes(entityPairs)

          let insertPosition = this._parsePosition(attributes, 10, 20)
          let block = blockList.find(block => block.name == attributes[2])
          if(block) {

            // Insert entities from block to the entity list
            block.entityList.forEach(entity => {
              // TODO: Handle rotation/scale

              var entityCopy = entity
              if(entityCopy.startPosition)
                entityCopy.startPosition = entityCopy.startPosition.clone().add(insertPosition)
              if(entityCopy.endPosition)
                entityCopy.endPosition = entityCopy.endPosition.clone().add(insertPosition)
              if(entityCopy.arcCenterPosition)
                entityCopy.arcCenterPosition = entityCopy.arcCenterPosition.clone().add(insertPosition)
              if(entityCopy.position)
                entityCopy.position = entityCopy.position.clone().add(insertPosition)
              if(_.isObject(entityCopy.vertices))
                entityCopy.vertices = entityCopy.vertices.map(v => v.clone().add(insertPosition))

              entityList.push(entityCopy)
            })
          }
        }
      }

      this._skipToNextEntity()
    }

    return entityList
  }


  /**
   * Parse a LWPolyline entity vertex data and return in an array containing position & bulge data
   */
  _parseLWPolylineVertexData(entityAttributes, polylineEntityPairs) {
    var vertexDataList = []
    var vertex = null

    var polylineFlag = parseInt(entityAttributes[70] || 0)
    var isClosed = polylineFlag & 1

    polylineEntityPairs.forEach(pair => {
      switch(pair.code) {
        case 10:
          vertex = {
            x: parseFloat(pair.value) * this.unitsScale,
            y: 0,
            bulge: 0
          }
          vertexDataList.push(vertex)
          break
        case 20:
          if(vertex)
            vertex.y = -parseFloat(pair.value) * this.unitsScale    // Flip into our coord system
          break
        case 42:
          if(vertex) {
            vertex.bulge = parseFloat(pair.value)

            // Bulge flipped to match our coordinate system
            if(vertex.bulge != 0.0)
              vertex.bulge = -vertex.bulge
          }
          break
      }
    })

    // If the entity is flagged as closed & the end point does not meet the start point already, close the shape
    if(vertexDataList.length > 0 && isClosed) {
      let connectedMinDist = 0.1

      if(Math.abs(vertexDataList[vertexDataList.length-1].x - vertexDataList[0].x) > connectedMinDist ||
         Math.abs(vertexDataList[vertexDataList.length-1].y - vertexDataList[0].y) > connectedMinDist) {
          vertexDataList.push(vertexDataList[0])
      }
    }

    return vertexDataList
  }


  /**
   * Convert the vertex data from LWPolyline entity into raw vertex point list
   * Returns an array of Vector2 objects
   */
  _convertLWPolylineVertices(vertexDataList) {
    var outputVertices = []

    // Convert vertices with bulges into an array of vertices with arcs for bulges
    for(var i=0; i<vertexDataList.length-1; i++) {
      let start = new THREE.Vector2(vertexDataList[i].x, vertexDataList[i].y)
      let end = new THREE.Vector2(vertexDataList[i+1].x, vertexDataList[i+1].y)

      outputVertices.push(start)
      if(vertexDataList[i].bulge != 0.0) {
        // Generate an arc with the bulge
        // Calculates the subdivided vertices instead of an ARC entity
        outputVertices = outputVertices.concat(this._generatePointsForLWPolyline(start, end, vertexDataList[i].bulge, 5))
      }

      // Last vert
      if(i == vertexDataList.length-2)
        outputVertices.push(end)
    }

    return outputVertices
  }


  /**
   * Calculate the arc details for an LWPolyline segment containing a bulge
   * Returns an object with the arc details
   */
  _calculateArcForLWPolyline(start, end, bulge) {
    // Note: If bulge is < 0, the arc is clockwise. Otherwise counter-clockwise

    var arcTheta = 0
    var arcA, arcB
    if(bulge < 0) {
      arcTheta = Math.atan(-bulge) * 4
      arcA = start.clone()
      arcB = end.clone()
    } else {
      arcTheta = Math.atan(bulge) * 4
      arcA = end.clone()
      arcB = start.clone()
    }

    let seg = arcB.clone().sub(arcA)
    let segLength = seg.length()
    let c = arcA.clone().addScaledVector(seg, 0.5)

    let lengthCD = Math.abs((segLength / 2) / Math.tan(arcTheta / 2))
    let normAB = seg.clone()
    normAB.normalize()

    var arcCenter
    if(arcTheta < Math.PI) {
      let normDC = new THREE.Vector2(
        normAB.x * Math.cos(Math.PI / 2) - normAB.y * Math.sin(Math.PI / 2),
        normAB.y * Math.cos(Math.PI / 2) + normAB.x * Math.sin(Math.PI / 2)
      )
      arcCenter = c.clone().addScaledVector(normDC, -lengthCD)

    } else {
      let normDC = new THREE.Vector2(
        normAB.x * Math.cos(Math.PI / 2) - normAB.y * Math.sin(Math.PI / 2),
        normAB.y * Math.cos(Math.PI / 2) + normAB.x * Math.sin(Math.PI / 2)
      )
      arcCenter = c.clone().addScaledVector(normDC, lengthCD)
    }

    let startAngle = Math.atan2(arcB.y - arcCenter.y, arcB.x - arcCenter.x) / Math.PI * 180
    var endAngle = Math.atan2(arcA.y - arcCenter.y, arcA.x - arcCenter.x) / Math.PI * 180
    if(endAngle < startAngle)
      endAngle += 360

    let radius = arcB.clone().sub(arcCenter).length()

    return {
      radius: radius,
      startAngle: startAngle,
      endAngle: endAngle,
      center: arcCenter
    }
  }


  /**
   * Calculate the arc & subdivide into the vertices that make up an arc in a LWPolyline
   * Subdivides the arc into points and returns the points
   */
  _generatePointsForLWPolyline(start, end, bulge, subdivisionCount = 5) {
    let arcInfo = this._calculateArcForLWPolyline(start, end, bulge)    

    // Interpolate points
    let startInt = Math.floor(arcInfo.startAngle / subdivisionCount) * subdivisionCount + subdivisionCount
    let endInt = Math.ceil(arcInfo.endAngle / subdivisionCount) * subdivisionCount - subdivisionCount

    var vertices = []
    for(var i=startInt; i<=endInt; i+=subdivisionCount) {
      vertices.push(arcInfo.center.clone().add(
        new THREE.Vector2(
          Math.cos(i / 180 * Math.PI) * arcInfo.radius,
          Math.sin(i / 180 * Math.PI) * arcInfo.radius
        ))
      )
    }
    if(bulge < 0)
      vertices.reverse()
    return vertices    
  }


  /**
   * Convert a Spline entity into a polyline entity
   */
  _convertSplineToPolylineEntity(entityAttributes, entityPairs) {
    let polylineEntity = {
      type: 'POLYLINE',
      name: entityAttributes[2],
      layer: entityAttributes[8],
      handle: entityAttributes[5],
      colourNumber: parseInt(entityAttributes[62] || 0),
      vertices: [],
      vertexData: [],
      startPosition: null,
      endPosition: null,
    }

    let subDivisionCount = 4
    let flags = parseInt(entityAttributes[70] || 0)
    let splineCurveDegree = parseInt(entityAttributes[71] || 0)
    let knotValues = []
    let controlPoints = []

    let curControlPoint = null

    entityPairs.forEach(pair => {
      switch(pair.code) {
        case 10:
          curControlPoint = {
            x: parseFloat(pair.value) * this.unitsScale,
            y: 0
          }
          controlPoints.push(curControlPoint)
          break
        case 20:
          if(curControlPoint)
            curControlPoint.y = -parseFloat(pair.value) * this.unitsScale    // Flip into our coord system
          break

        case 40:
          knotValues.push(parseFloat(pair.value))
          break
      }
    })

    let subCount = controlPoints.length * subDivisionCount
    let step = 1 / subCount
    for (let i = 0; i <= subCount; i++) {
      let vertPoint = this._interpolateSplineControlPoint(i * step, splineCurveDegree, controlPoints, knotValues)
      polylineEntity.vertices.push(vertPoint)
    }

    return polylineEntity
  }

  _interpolateSplineControlPoint(fraction, splineCurveDegree, controlPoints, knotValues) {
    let internalPoints = controlPoints.map(p => [p.x, p.y])

    if(splineCurveDegree < 1)
      throw 'DXF invalid spline info'
    if(splineCurveDegree > (internalPoints.length - 1))
      throw 'DXF invalid spline info (Bad Degree)'
    if(knotValues.length !== internalPoints.length + splineCurveDegree + 1)
      throw 'DXF invalid spline info (Bad Knots)'


    let weights = []
    for(let i=0; i<internalPoints.length; i++)
      weights.push(1)

    let domain = [splineCurveDegree, knotValues.length - 1 - splineCurveDegree]
    
    let low  = knotValues[domain[0]]
    let high = knotValues[domain[1]]
    fraction = fraction * (high - low) + low

    if(fraction < low)
      fraction = low
    else if(fraction > high)
      fraction = high

    let splineSeg = 0
    for (splineSeg = domain[0]; splineSeg < domain[1]; splineSeg++) {
      if (fraction >= knotValues[splineSeg] && fraction <= knotValues[splineSeg + 1])
        break
    }

    // Convert points to homogeneous coordinates
    const v = []
    for(let i = 0; i<internalPoints.length; i++) {
      v[i] = []
      for (let j = 0; j < 2; j++)
        v[i][j] = internalPoints[i][j] * weights[i]

      v[i][2] = weights[i]
    }

    // l (level) goes from 1 to the curve degree + 1
    let alpha
    for(let l = 1; l <= splineCurveDegree + 1; l++) {
        // build level l of the pyramid
        for(let i = splineSeg; i > splineSeg - splineCurveDegree - 1 + l; i--) {
            alpha = (fraction - knotValues[i]) / (knotValues[i + splineCurveDegree + 1 - l] - knotValues[i])
            // interpolate each component
            for(let j = 0; j < 2 + 1; j++) {
                v[i][j] = (1 - alpha) * v[i - 1][j] + alpha * v[i][j]
            }
        }
    }

    // Convert back to cartesian and return
    const result = []
    for(let i = 0; i < 2; i++) {
        result[i] = v[splineSeg][i] / v[splineSeg][2]
    }

    return new THREE.Vector2(result[0], result[1])
  }


  /**
   * Skip the read index to be at the next available entity group code
   */
  _skipToNextEntity() {
    while(this.currentPairIndex < this.dxfPairList.length) {
      if(this.dxfPairList[this.currentPairIndex].code == 0)
        break

      this.currentPairIndex++
    }
  }


  /**
   * Read the next pair in the list & increment the read index
   * Returns the pair details
   */
  _readNextPair() {
    if(this.currentPairIndex >= this.dxfPairList.length)
      return null

    return this.dxfPairList[this.currentPairIndex++]
  }


  /**
   * Read the next pair in the list without incrementing the read index
   * Returns the pair details
   */
  _peekNextPair() {
    if(this.currentPairIndex >= this.dxfPairList.length)
      return null

    return this.dxfPairList[this.currentPairIndex]
  }


  /**
   * Read the entity attributes as an array of pairs
   * Used for entities that contain repeating attributes (eg, vertex list)
   */
  _readEntityAttributePairs() {
    var pairList = []
    var pair = null
    while((pair = this._peekNextPair())) {
      if(pair.code == 0)
        break

      pairList.push(this._readNextPair())
    }

    return pairList
  }


  /**
   * Parse all the attributes for an entity up until the next entity object
   * Returns an object with the group codes as keys
   */
  _parseEntityAttributes(entityPairList) {
    var attributes = {}

    entityPairList.forEach(pair => {
      attributes[pair.code] = pair.value
    })

    return attributes
  }


  /**
   * Reverse an entity object so the start/end details are reversed
   */
  _reverseEntity(entity) {
    var outputEntity = {
      type: entity.type,
      layer: entity.layer,
      name: entity.name,
      handle: entity.handle,
      lineType: entity.lineType,
      colourNumber: entity.colourNumber,
      vertices: []
    }

    if(entity.type == 'ARC') {
      outputEntity.arcCenterPosition = entity.arcCenterPosition.clone()
      outputEntity.radius = entity.radius
      outputEntity.startAngle = entity.endAngle
      outputEntity.endAngle = entity.startAngle
      outputEntity.clockwise = !entity.clockwise

      outputEntity.arcEllipseCurve = new THREE.EllipseCurve(
        outputEntity.arcCenterPosition.x,
        outputEntity.arcCenterPosition.y,
        outputEntity.radius,
        outputEntity.radius,
        MathsUtils.degreesToRadians(outputEntity.startAngle),
        MathsUtils.degreesToRadians(outputEntity.endAngle)
      )

      outputEntity.startPosition = outputEntity.arcEllipseCurve.getPoint(0, new THREE.Vector2())
      outputEntity.endPosition = outputEntity.arcEllipseCurve.getPoint(1, new THREE.Vector2())
    }

    if(entity.type == 'LINE') {
      outputEntity.startPosition = entity.endPosition.clone()
      outputEntity.endPosition = entity.startPosition.clone()
    }

    if(entity.type == 'POLYLINE') {
      // Reverse the vertices
      outputEntity.vertices = entity.vertices.reverse().map(vert => vert.clone())

      if(_.isArray(outputEntity.vertexData)) {
        outputEntity.vertexData = entity.vertexData.reverse()
        // Flip bulges
        outputEntity.vertexData.forEach(vertex => {
          if(vertex.bulge != 0.0)
            vertex.bulge = -vertex.bulge
        })
      }

      outputEntity.startPosition = outputEntity.vertices[0].clone()
      outputEntity.endPosition = outputEntity.vertices[outputEntity.vertices.length - 1].clone()
    }

    return outputEntity
  }


  /**
   * Reverse the segment list
   * Used to for shapes that are anti-clockwise
   */
  _reverseSegmentList(segmentList) {
    var outputSegmentList = []

    for(var i=0; i<segmentList.length; i++) {
      var outputSegment = segmentList[i].clone()

      outputSegment.type = segmentList[segmentList.length-i-1].type

      if(segmentList[segmentList.length-i-1].type == GlassDesignerTypes.SegmentTypes.arc) {
        outputSegment.arcCenterPosition = segmentList[segmentList.length-i-1].arcCenterPosition.clone()
        outputSegment.arcRadius = segmentList[segmentList.length-i-1].arcRadius
        outputSegment.arcStartAngle = segmentList[segmentList.length-i-1].arcEndAngle
        outputSegment.arcEndAngle = segmentList[segmentList.length-i-1].arcStartAngle
        outputSegment.arcClockwise = !segmentList[segmentList.length-i-1].arcClockwise
      }

      outputSegment.startPosition = segmentList[segmentList.length-i-1].endPosition.clone()
      outputSegment.endPosition = segmentList[segmentList.length-i-1].startPosition.clone()

      outputSegment.startCornerRadius = segmentList[segmentList.length-i-1].endCornerRadius
      outputSegment.endCornerRadius = segmentList[segmentList.length-i-1].startCornerRadius

      outputSegmentList.push(outputSegment)
    }

    return outputSegmentList
  }


  /**
   * Run a filter removing any lines that are completely overlapped by another line
   * 
   * @param {Array<Object>} entityList - Array of objects containing entities parsed from the DXF file
   * @return {Array<Object>} Returns entity list with overlapped lines removed
   */
  _filterOverlappedLines(entityList) {
    var outputList = []
    var overlapTolerance = 0.1

    for(let i=0; i<entityList.length; i++) {
      let entity = entityList[i]

      // Non-Line entities stay
      if(entity.type != 'LINE') {
        outputList.push(entity)
        continue
      }

      let hasOverlap = false

      // Check other lines if they overlap this line
      for(let n=0; n<entityList.length && !hasOverlap; n++) {
        if(i == n)
          continue

        let otherLine = entityList[n]
        if(otherLine.type != 'LINE')
          continue

        // If both start & end points of this line exist somewhere on the other line, it's overlapped
        let closestStartPoint = MathsUtils.getClosest2DPointOnLineSegment(otherLine.startPosition, otherLine.endPosition, entity.startPosition, true)
        let closestEndPoint = MathsUtils.getClosest2DPointOnLineSegment(otherLine.startPosition, otherLine.endPosition, entity.endPosition, true)

        if(closestStartPoint.distanceTo(entity.startPosition) <= overlapTolerance && closestEndPoint.distanceTo(entity.endPosition) <= overlapTolerance) {
          // Overlapping
          hasOverlap = true
        }        
      }

      if(hasOverlap)
        continue

      outputList.push(entity)
    }

    return outputList
  }


  /**
   * Build the list of shapes from a collection of loose entities from the DXF file
   * 
   * @param {Array<Object>} entityList - Array of objects containing entities parsed from the DXF
   * @param {Object} importOptions - Object containing options to control shape detection. Properties include:
   *    sameLineTypeOnly - If set, only lines with same line type can be joined to form a shape
   *    sameLayerOnly - If set, only lines with same layer can be joined to form a shape
   *    sameColourOnly - If set, only lines with the same colours can be joined to form a shape
   *    ignoreOverlappingLines - If set, ignores lines that are completely overlapped
   */
  _buildShapesFromEntities(entityList, importOptions) {
    let connectedMinDist = 1.0
    let shapeMinSize = 5
    let defaultCutoutRadius = 10
    var closedEntityGroups = []

    // Ignore circles/points for outside shapes
    let shapeEntityList = entityList.filter(entity => entity.type == 'ARC' || entity.type == 'LINE' || entity.type == 'POLYLINE')
    let pointCircleList = entityList.filter(entity => entity.type == 'POINT' || entity.type == 'CIRCLE')

    // Filter out lines that are completely overlapped by another line
    if(importOptions.ignoreOverlappingLines)
      shapeEntityList = this._filterOverlappedLines(shapeEntityList)


    // TODO: Handle a large circle as a shape

    var curShape = []
    while(shapeEntityList.length > 0) {
      curShape = [shapeEntityList.pop()]

      let parsingEntities = true
      while(parsingEntities) {
        let lastEntity = curShape[curShape.length - 1]
        
        let prevEndPos = lastEntity.endPosition

        // Loop closed? Add as a shape
        if(curShape[0].startPosition.distanceTo(prevEndPos) < connectedMinDist) {
          closedEntityGroups.push(curShape)
          curShape = []
          break
        }

        let length = shapeEntityList.length
        var foundConnection = false
        for(var j=0; j<length; j++) {
          let entity = shapeEntityList[j]

          // Ignore lines on different layers if set
          if(entity.layer != lastEntity.layer && importOptions.sameLayerOnly)
            continue

          // Ignore lines with different colour numbers if set
          if(entity.colourNumber != lastEntity.colourNumber && importOptions.sameColourOnly)
            continue

          // Ignore lines of different line types if set
          if(entity.lineType != lastEntity.lineType && importOptions.sameLineTypeOnly)
            continue

          if(entity.startPosition.distanceTo(prevEndPos) < connectedMinDist) {
            curShape.push(entity)
            shapeEntityList.splice(j, 1)
            foundConnection = true
            break
          }
          if(entity.endPosition.distanceTo(prevEndPos) < connectedMinDist) {
            curShape.push(this._reverseEntity(entity))

            shapeEntityList.splice(j, 1)
            foundConnection = true
            break
          }
        }

        if(!foundConnection)
					break
      }
    }

    // Check remaining shapes
    // If a single polyline, close it & add as an entity
    if(curShape.length == 1) {
      if(curShape[0].type == 'POLYLINE') {
        // TODO: Check if not already closed
        if(curShape[0].vertices.length > 0) {
          curShape[0].vertices.push(curShape[0].vertices[0].clone())
        }
        if(_.isArray(curShape[0].vertexData) && curShape[0].vertexData.length > 0) {
          curShape[0].vertexData.push({
            x: curShape[0].vertexData[0].x,
            y: curShape[0].vertexData[0].y,
            bulge: curShape[0].vertexData[0].bulge
          })
        }

        if(curShape[0].vertices.length)
          closedEntityGroups.push(curShape)
      }
    }

    var outputShapes = []

    closedEntityGroups.forEach((entityGroup, index) => {
      var warningList = []

      var bounds = new THREE.Box2()
      entityGroup.forEach(entity => {
        bounds.expandByPoint(entity.startPosition)
        bounds.expandByPoint(entity.endPosition)

        // If an arc, go through & add all points around the arc to the bounds
        if(entity.type == 'ARC') {
          var arcSubdivisions = Math.max(20, Math.ceil(entity.arcEllipseCurve.getLength() / 75))
          var arcPoints = entity.arcEllipseCurve.getPoints(arcSubdivisions)

          arcPoints.forEach(shapePnt => {
            bounds.expandByPoint(shapePnt)
          })
        }

        if(entity.type == 'POLYLINE') {
          entity.vertices.forEach(vertex => {
            bounds.expandByPoint(vertex)
          })
        }
      })

      let boundsWidth = bounds.max.x - bounds.min.x
      let boundsHeight = bounds.max.y - bounds.min.y
      
      // Ignore if too small
      if(boundsHeight < shapeMinSize || boundsWidth < shapeMinSize) {
        console.warn(`[DXF] Import bounds too small [${boundsWidth}x${boundsHeight}]`)
        return
      }

      let shapePointList = []

      // Convert to segments
      var segmentList = []
      entityGroup.forEach(entity => {
        var segmentInfo = {
          type: GlassDesignerTypes.SegmentTypes.line,
          startCornerRadius: 0,
          endCornerRadius: 0,
          startPosition: entity.startPosition.clone(),
          endPosition: entity.endPosition.clone()
        }
        if(entity.type == 'ARC') {
          segmentInfo.type = GlassDesignerTypes.SegmentTypes.arc
          segmentInfo.arcRadius = entity.radius
          segmentInfo.arcStartAngle = entity.startAngle
          segmentInfo.arcEndAngle = entity.endAngle
          segmentInfo.arcClockwise = entity.clockwise
          segmentInfo.arcCenterPosition = entity.arcCenterPosition.clone()
        }

        if(entity.type == 'POLYLINE') {
          // If vertex data is available, use that instead as it contains the arc details
          if(_.isArray(entity.vertexData) && entity.vertexData.length > 0) {

            for(let vertIndex=0; vertIndex<entity.vertexData.length-1; vertIndex++) {
              let start = new THREE.Vector2(entity.vertexData[vertIndex].x, entity.vertexData[vertIndex].y)
              let end = new THREE.Vector2(entity.vertexData[vertIndex+1].x, entity.vertexData[vertIndex+1].y)

              let vertSegmentInfo = {
                type: GlassDesignerTypes.SegmentTypes.line,
                startCornerRadius: 0,
                endCornerRadius: 0,
                startPosition: start,
                endPosition: end
              }
              // If bulge is set, create an arc
              if(entity.vertexData[vertIndex].bulge != 0.0) {
                let arcInfo = this._calculateArcForLWPolyline(start, end, entity.vertexData[vertIndex].bulge)

                vertSegmentInfo.type = GlassDesignerTypes.SegmentTypes.arc
                vertSegmentInfo.arcCenterPosition = arcInfo.center.clone()
                vertSegmentInfo.arcRadius = arcInfo.radius
                vertSegmentInfo.arcClockwise = entity.vertexData[vertIndex].bulge > 0
                vertSegmentInfo.arcStartAngle = MathsUtils.calculateArcPointAngle(vertSegmentInfo.arcCenterPosition, vertSegmentInfo.startPosition)
                vertSegmentInfo.arcEndAngle = MathsUtils.calculateArcPointAngle(vertSegmentInfo.arcCenterPosition, vertSegmentInfo.endPosition)
              }
              shapePointList.push(vertSegmentInfo.startPosition)
              segmentList.push(new GlassSegment(vertSegmentInfo.type, vertSegmentInfo))
            }

          } else {
            // Generate segments between all the vertices
            for(let vertIndex=0; vertIndex<entity.vertices.length-1; vertIndex++) {
              let vertSegmentInfo = {
                type: GlassDesignerTypes.SegmentTypes.line,
                startCornerRadius: 0,
                endCornerRadius: 0,
                startPosition: entity.vertices[vertIndex].clone(),
                endPosition: entity.vertices[vertIndex+1].clone()
              }
              shapePointList.push(vertSegmentInfo.startPosition)
              segmentList.push(new GlassSegment(vertSegmentInfo.type, vertSegmentInfo))
            }
          }
          return
        }

        shapePointList.push(entity.startPosition)
        segmentList.push(new GlassSegment(segmentInfo.type, segmentInfo))
      })

      if(MathsUtils.is2DPolygonClosed(shapePointList))
        shapePointList.pop()
      
      // Make sure the segment list is clockwise
      if(MathsUtils.get2DPolygonOrientation(shapePointList) > 0)
        segmentList = MathsUtils.reverseGlassSegmentList(segmentList)

      // If the shape is self intersecting, ignore it
      // Happens for info-blocks that get recognised as shapes
      if(MathsUtils.checkGlassSegmentListSelfIntersects(segmentList)) {
        console.warn('[DXF] Closed shape self-intersects')
        return
      }


      // Calculate arc orientation values based on position in shape
      segmentList.forEach((segment, index) => {
        let prevSeg = segmentList[index == 0 ? segmentList.length-1 : index-1]
        let nextSeg = segmentList[index == segmentList.length-1 ? 0 : index+1]

        if(segment.type == GlassDesignerTypes.SegmentTypes.arc) {
          var prevSegDir = prevSeg.endPosition.clone().sub(prevSeg.startPosition)
          prevSegDir.normalize()
          prevSegDir.negate()
          var nextSegDir = nextSeg.endPosition.clone().sub(nextSeg.startPosition)
          nextSegDir.normalize()

          let prevAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(prevSegDir)))
          var nextAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(nextSegDir)))

          // This is not 100% foolproof, but we'll convert small arcs to corner radiuses instead for now
          // Revisit this in the future if it becomes an issue for large arcs
          /*if(prevAngle > nextAngle && prevAngle - nextAngle >= 180)
            segment.arcClockwise = false
          else
            segment.arcClockwise = true*/
        }
      })
      


      // Find interior points/circles
      var holeList = []
      pointCircleList.forEach(entity => {
        if(MathsUtils.pointIn2DPolygon(entity.position, shapePointList)) {

          if(entity.type == 'POINT')
            warningList.push('Point entities have been used for center cutouts without radius. A default radius will be used')

          var holeInfo = {
            holeType: GlassDesignerTypes.LibraryItemShapeTypes.circle,
            position: entity.position.clone(),
            radius: entity.radius || defaultCutoutRadius,
          }
          holeList.push(holeInfo)
        }
      })

      outputShapes.push({
        bounds: bounds,
        width: MathsUtils.roundNumber(boundsWidth, 2),
        height: MathsUtils.roundNumber(boundsHeight, 2),
        segmentList: segmentList,
        holeList: holeList,
        warningList: _.uniq(warningList)
      })
    })
   
    // If shapes exist entirely inside of larger shapes, remove from shape list & add as a custom hole
    // Eg, powerpoints
    var foundHole = true
    while(foundHole) {

      foundHole = false
      for(var i=0; i<outputShapes.length && !foundHole; i++) {
        var outerShape = outputShapes[i]

        for(let j=0; j<outputShapes.length && !foundHole; j++) {
          var innerShape = outputShapes[j]
          if(i == j)
            continue

          if(outerShape.bounds.containsBox(innerShape.bounds)) {
            var holeData = {
              holeType: GlassDesignerTypes.LibraryItemShapeTypes.custom,
              position: innerShape.bounds.getCenter(new THREE.Vector2()),
              segmentList: []
            }

            // Adjust the segments to be centered around the position
            holeData.segmentList = innerShape.segmentList.map(segmentInfo => {
              segmentInfo.startPosition.sub(holeData.position)
              segmentInfo.endPosition.sub(holeData.position)

              if(segmentInfo.type == GlassDesignerTypes.SegmentTypes.arc) {
                segmentInfo.arcCenterPosition.sub(holeData.position)
              }

              return segmentInfo
            })

            // Create a custom center cutout & remove it from the imported shape list
            outerShape.holeList.push(holeData)
            outputShapes.splice(j, 1)
            foundHole = true
          }        
        }
      }
    }


    // Go through all shapes & detect any overlapping circle holes
    // Flag warnings if overlapping holes are found
    outputShapes.forEach(shape => {
      let overlapCount = 0

      shape.holeList.forEach((hole, index) => {
        for(let j=index+1; j<shape.holeList.length; j++) {
          let otherHole = shape.holeList[j]

          // Only check circles for overlap
          if(hole.holeType != GlassDesignerTypes.LibraryItemShapeTypes.circle || otherHole.holeType != GlassDesignerTypes.LibraryItemShapeTypes.circle)
            continue

          let dist = hole.position.distanceTo(otherHole.position)
          if(dist < (hole.radius + otherHole.radius)) {
            overlapCount++
          }          
        }
      })

      if(overlapCount > 0) {
        shape.warningList.push(`Contains ${overlapCount} overlapping center cutout(s)`)
      }
    })

    return outputShapes
  }
   


  /**
   * Attempt to find the shapes in the DXF data
   * 
   */
  findShapeObjectList(importOptions = {}) {
    // Build entity list
    var looseEntityList = this._buildEntityList()

    let shapeList = this._buildShapesFromEntities(looseEntityList, importOptions)

    return shapeList
  }
}

